import React, { Fragment } from "react";
import PropTypes from "prop-types";
import Combobox from "../../common/SelectInput";
import AutoComplete from "../../common/AutoComplete";
import Table from "../../common/Table";
import Button from "../../common/Button";
import * as types from "../../../constants/ComponentConstants";
import DateComponent from "../../common/Date";
import Section from "../../common/Section";
import moment from "moment";
import RelativeLink from "../../common/RelativeLink";
import * as DomainConstants from '../../../constants/DomainConstants';
import InSamenWerkingMetLogos from "../../common/InSamenWerkingMetLogos";
import Row from "../../common/Row";
import Fieldset from "../../common/Fieldset";
import ANBLogo from "../../common/svg/ANBLogo";
import ANBLogoAfgekeurd from "../../common/svg/ANBLogoAfgekeurd";
import AWVLogo from "../../common/svg/AWVLogo";
import AWVLogoAfgekeurd from "../../common/svg/AWVLogoAfgekeurd";
import SVLLogo from "../../../images/logo-svl.png";
import UIVLogo from "../../../images/logo-ui-t-in-vlaanderen.png";

const statusFilterOpties = [
    { value: DomainConstants.TochtRedenVanStatus.InAfwachtigingVanGoedkeuringDoorFedKoepel, label: types.AFWACHTING_FEDERATIE_OVERKOEPELENDE_ACTOR },
    { value: DomainConstants.TochtRedenVanStatus.AfgekeurdDoorFedKoepel, label: types.AFGEKEURD_FEDERATIE_OVERKOEPELENDE_ACTOR },
    { value: DomainConstants.TochtRedenVanStatus.InAfwachtigingVanGoedkeuringDoorSVL, label: types.AFWACHTING_SPORT_VLAANDEREN },
    { value: DomainConstants.TochtRedenVanStatus.DossierOnvolledig, label: types.DOSSIER_ONVOLLEDIG },
    { value: DomainConstants.TochtRedenVanStatus.DossierVolledig, label: types.DOSSIER_VOLLEDIG },
    { value: DomainConstants.TochtRedenVanStatus.DoorgestuurdTerGoedkeuring, label: types.DOORGESTUURD_TER_GOEDKEURING }
];

const FederatieOverzichtForm = (props) => {
    const {
        model,
        provincies,
        onChangeProvincie,
        provincieFilter,
        statusFilter,
        onChangeStatus,
        hoofdgemeenten,
        gemeenteFilter,
        onChangeGemeente,
        onKeurKalenderGoed
    } = props;

    const overzichtTabelHeaderSizes = [24, 12, 22, 30, 12];
    const statusOpties = model.tochtenAutomatischGoedkeuren
        ? statusFilterOpties.filter(x => x.value !== DomainConstants.TochtRedenVanStatus.InAfwachtigingVanGoedkeuringDoorFedKoepel && x.value !== DomainConstants.TochtRedenVanStatus.AfgekeurdDoorFedKoepel)
        : statusFilterOpties;

    function getTochtenRows(tochten, readonly, toonIconen) {
        return tochten.map((tocht, i) => {
            const gepubliceerdBijSportVlaanderen = tocht.gepubliceerdBijSportVlaanderen && toonIconen
            ? <img title="gepubliceerd op de website van Sport Vlaanderen" className="extern-logo" src={SVLLogo} />
            : null;

            const gepubliceerdBijUitInVlaanderen = tocht.gepubliceerdBijUitInVlaanderen && toonIconen
            ? <img title="gepubliceerd op de website van UiT in Vlaanderen" className="extern-logo" src={UIVLogo} />
            : null;

			const ANBIcoon = !toonIconen ? null : tocht.goedgekeurdDoorANB === true ? <span title="goedgekeurd door Agentschap voor Natuur en Bos">{ANBLogo()}</span> : tocht.goedgekeurdDoorANB === false ? <span title="afgekeurd door Agentschap voor Natuur en Bos">{ANBLogoAfgekeurd()}</span> : null;
			const AWVIcoon = !toonIconen ? null : tocht.goedgekeurdDoorAWV === true ? <span title="goedgekeurd door Agentschap Wegen en Verkeer">{AWVLogo()}</span> : tocht.goedgekeurdDoorAWV === false ? <span title="afgekeurd door Agentschap Wegen en Verkeer">{AWVLogoAfgekeurd()}</span> :  null;

            const icons = toonIconen ? <Fragment><br />{gepubliceerdBijSportVlaanderen}{gepubliceerdBijUitInVlaanderen}{ANBIcoon}{AWVIcoon}</Fragment> : null;

            return (<tr key={i}>
                <td>{tocht.organisatorNaam}</td>
                <td className="fit-content"><DateComponent value={tocht.datum} /></td>
                <td>{tocht.startgemeenteNaam}</td>
                <td>
                    {tocht.redenVanStatusNaam}
                    {icons}
                </td>
                <td className="fit-content"><RelativeLink to={"/e-loket/organisator/" + tocht.id + "/federatie" + "/" + readonly} title="detail tocht"><i className="fa fa-file-text-o fa-fw"></i></RelativeLink></td>
            </tr>);
        });
    }

    function handleOnKeurKalenderGoed() {
        return onKeurKalenderGoed("provincieIdFuture");
    }

    const futureTochtenWithoutProvincieFilter = model.tochten.filter(t => (moment().diff(moment(t.datum)) < 0 && t.isFiltered == false)).reverse();
    const futureTochten = futureTochtenWithoutProvincieFilter
        .filter(x => x.provincieId === provincieFilter["provincieIdFuture"].toString() || provincieFilter["provincieIdFuture"].toString() === "")
        .filter(x => String(statusFilter["statusFuture"]) === "" || String(x.redenVanStatus) === String(statusFilter["statusFuture"]));
    const futureTochtenTable = getTochtenRows(futureTochten, false, true);

    const pastTochtenWithoutGemeenteFilter = model.tochten.filter(t => (moment().diff(moment(t.datum)) >= 0 && t.isFiltered == false));
    const pastTochten = pastTochtenWithoutGemeenteFilter.filter(x => gemeenteFilter["gemeentePast"] === "" || (x.gemeenteIds && x.gemeenteIds.includes(gemeenteFilter["gemeentePast"])));
    const pastTochtenTable = getTochtenRows(pastTochten, true, false);

    const typeOrganisatie = model.isKoepelOrganisatie ? "organisatie" : "sportfederatie";
    function scrollToElement(sectionId) {
        return () => {
            const section = document.querySelector(sectionId);
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
    }
    return (
        <Fragment>
            <Section
                classes="body-bg">
                <Row>
                    <Button
                        text="Te organiseren tochten"
                        onClick={scrollToElement('.geplandeTochten')}
                    />
                    {(pastTochtenTable && pastTochtenTable.length > 0) &&
                        <Button
                            text="Afgelopen tochten"
                            onClick={scrollToElement('.geredenTochten')}
                        />
                    }
                </Row>
            </Section>

            <Section
                title={`Tochten die door een lid van jouw ${typeOrganisatie} georganiseerd worden`}
                color={types.COLOR_BLUE}
                id="geplandeTochten"
                canScrollToTop>
                {!model.tochtenAutomatischGoedkeuren && futureTochtenWithoutProvincieFilter.length > 0 &&
                    <Fieldset>
                        <Button
                            text="Keur kalender goed"
                            onClick={handleOnKeurKalenderGoed} />
                    </Fieldset>}

                {futureTochtenWithoutProvincieFilter.length > 0 &&
                    <Fieldset>
                        <Combobox
                            label="Provincie"
                            items={provincies}
                            name="provincieIdFuture"
                            onChange={onChangeProvincie}
                            value={provincieFilter && provincieFilter["provincieIdFuture"] && provincieFilter["provincieIdFuture"].toString()}
                            defaultItem={"Alle provincies"}
                            margin="0px"
                            applySizeClass={false}
                        />
                        <Combobox
                            label="Status"
                            items={statusOpties}
                            name="statusFuture"
                            onChange={onChangeStatus}
                            value={statusFilter["statusFuture"]}
                            defaultItem={"Alle statussen"}
                            margin="8px"
                            applySizeClass={false}
                        />
                    </Fieldset>}

                {(futureTochten.length > 0) ?
                    <Section>
                        <Table
                            headers={["organisator", "datum", "startgemeente", "status", ""]}
                            headerSizes={overzichtTabelHeaderSizes}
                        >
                            {futureTochtenTable}
                        </Table>
                    </Section>
                    :
                    <Section >
                        <Fieldset>
                            <p>Geen tochten gevonden die voldoen aan de opgegeven filtercriteria</p>
                        </Fieldset>
                    </Section>}
            </Section>

            {(pastTochtenWithoutGemeenteFilter.length > 0) ?
                <Section
                    title={`Afgelopen tochten die door een lid van jouw ${typeOrganisatie} georganiseerd werden`}
                    color={types.COLOR_TURQUOISE_DARK}
                    id="geredenTochten"
                    canScrollToTop>
                    <Fieldset>
                        <AutoComplete
                            label="Gemeente"
                            name="gemeentePast"
                            items={hoofdgemeenten}
                            value={gemeenteFilter["gemeentePast"]}
                            onChange={onChangeGemeente}
                            defaultItem="Alle gemeenten"
                            clearable
                        />
                    </Fieldset>

                    {(pastTochten.length > 0) ?
                        <Section>
                            <Table
                                headers={["organisator", "datum", "startgemeente", "status", ""]}
                                headerSizes={overzichtTabelHeaderSizes}
                            >
                                {pastTochtenTable}
                            </Table>
                        </Section>
                        :
                        <Section >
                            <Fieldset>
                                <p>Geen tochten gevonden die voldoen aan de opgegeven filtercriteria</p>
                            </Fieldset>
                        </Section>
                    }
                </Section>
                :
                null}
            <InSamenWerkingMetLogos />
        </Fragment>);
};

FederatieOverzichtForm.propTypes = {
    model: PropTypes.object,
    provincies: PropTypes.array.isRequired,
    onChangeProvincie: PropTypes.func.isRequired,
    provincieFilter: PropTypes.object.isRequired,
    statusFilter: PropTypes.object.isRequired,
    onChangeStatus: PropTypes.func.isRequired,
    hoofdgemeenten: PropTypes.array,
    gemeenteFilter: PropTypes.object.isRequired,
    onChangeGemeente: PropTypes.func.isRequired,
    onKeurKalenderGoed: PropTypes.func.isRequired
};

export default FederatieOverzichtForm;
